import styled, { css } from 'styled-components';
import Image from 'next/image';
import {
  FlexBetween,
  FlexColumn,
  FlexibleDiv
} from '../../../../styled/common';
import { Typography } from '../../../ui/Typography';
import { Breakpoints } from '../../../../styled/constants';
import { tagStyles } from '../../../ui/Typography/styles';

export const apartmentGridTemplate = `
  60px
  100px
  150px
  115px
  115px
  100px
`;

export const ApartmentContainer = styled(FlexBetween)<{ $hasImage: boolean }>`
  gap: 5px;
  padding: 15px 0;
  height: 90px;
  width: 100%;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.secondary};
  transition:
    background-color 0.2s ease-in-out,
    border-radius 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  &:first-of-type {
    border-top: none;
  }

  &:focus {
    outline: none;
  }

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    padding: 15px 10px;
    display: grid;
    grid-template-columns: ${apartmentGridTemplate};

    &:last-of-type {
      border-bottom: 1px solid ${({ theme }) => theme.colors.border.secondary};
    }
  }

  ${({ $hasImage }) =>
    $hasImage &&
    css`
      @media (min-width: ${Breakpoints.smallDesktop}px) and (hover: hover) {
        cursor: pointer;

        &:hover {
          border-radius: 14px;
          background-color: ${({ theme }) => theme.colors.background.tertiary};
          border-top-color: transparent;
          border-bottom-color: transparent;
        }

        &:hover + & {
          border-top-color: transparent;
        }
      }
    `}
`;

export const ApartmentInfo = styled(FlexColumn)<{ $isModalView: boolean }>`
  gap: ${({ $isModalView }) => ($isModalView ? '10px' : '4px')};
  margin-bottom: ${({ $isModalView }) => ($isModalView ? '10px' : '0')};
  flex: 1;

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    display: contents;

    ${({ $isModalView }) =>
      $isModalView &&
      css`
        display: none;
      `};
  }
`;

export const ApartmentInfoArea = styled(Typography)<{ $isModalView: boolean }>`
  text-decoration-line: ${({ $isModalView }) =>
    $isModalView ? 'none' : 'underline'};

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    order: 2;
    text-decoration-line: none;
    display: flex;
    flex-direction: column;
    gap: 5px;

    span:nth-of-type(2) {
      ${tagStyles.body_1};
    }
  }
`;

export const ApartmentInfoPrice = styled(FlexibleDiv)`
  gap: 10px;

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    order: 3;
    display: flex;
    flex-direction: column;
    gap: 5px;

    span:nth-of-type(1) {
      ${tagStyles.title_4_new};
    }

    span:nth-of-type(2) {
      ${tagStyles.body_1};
    }
  }
`;

export const ApartmentPriceValue = styled(Typography)``;

export const ApartmentPricePerMeter = styled(ApartmentPriceValue)`
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const BottomApartmentInfo = styled(FlexibleDiv)`
  @media (max-width: ${({ theme }) => theme.smallDesktopBreakpoint}) {
    flex-wrap: wrap;
    align-items: center;
  }

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    display: contents;
  }
`;

export const DotDivider = styled.span`
  display: inline-block;
  width: 4px;
  height: 4px;
  background: #eae5e1;
  border-radius: 50%;
  margin: 0 5px;
  flex-shrink: 0;

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    display: none;
    margin: 0;
  }
`;

export const BottomApartmentInfoText = styled(Typography)<{
  $isTitleShown: boolean;
}>`
  @media (max-width: ${({ theme }) => theme.smallDesktopBreakpoint}) {
    white-space: nowrap;

    ${({ $isTitleShown }) =>
      $isTitleShown &&
      css`
        span:nth-of-type(1)::after {
          content: ': ';
        }
      `}
  }

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    order: 4;
    display: flex;
    flex-direction: column;
    gap: 5px;

    span:nth-of-type(1) {
      ${tagStyles.body_1};
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    span:nth-of-type(2) {
      ${tagStyles.body_1};
      text-transform: lowercase;
    }
  }
`;

export const ApartmentImage = styled(Image)<{ $hasImage: boolean }>`
  flex: 0 0 auto;
  height: 60px;
  width: 60px;
  border: 1px solid ${({ theme }) => theme.colors.border.secondary};
  border-radius: 7px;
  overflow: hidden;
  object-fit: ${({ $hasImage }) => ($hasImage ? 'contain' : 'cover')};
  background-color: ${({ theme }) => theme.colors.background.primary};

  @media (min-width: ${Breakpoints.smallDesktop}px) {
    order: 1;
  }
`;
